import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getUserAccountHistory } from "../../../common/services/auth/userAccountService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const UserAccountHistory = ({ userAccountId }) => {
  const { t } = useTranslation(["auth"]);
  const [userAccountHistory, setUserAccountHistory] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  const mapToViewModel = useCallback((userAccounts) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return userAccounts.map((userAccount) => ({
      id: Number(userAccount.id),
      userName: userAccount.login,
      firstName: userAccount.firstName,
      lastName: userAccount.lastName,
      email: userAccount.email,
      active: userAccount?.active?.toString(),
      updatedBy:
        userAccount.updatedByUserAccountFirstName +
        " " +
        userAccount.updatedByUserAccountLastName,
      updatedDate: new Date(userAccount.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(userAccount.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: userAccount.eventId,
    }));
  }, []);

  useEffect(() => {
    const fetchUserAccountHistory = async () => {
      try {
        const historyData = await getUserAccountHistory(userAccountId);
        const mappedData = mapToViewModel(historyData);
        setUserAccountHistory(_.orderBy(mappedData, "id", "desc"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserAccountHistory();
  }, [userAccountId, mapToViewModel]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: t("RecordID"), flex: 1 },
      { field: "userName", headerName: t("UserName"), flex: 1.5 },
      { field: "firstName", headerName: t("FirstName"), flex: 1.5 },
      { field: "lastName", headerName: t("LastName"), flex: 1.5 },
      { field: "email", headerName: t("Email"), flex: 2 },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      { field: "updatedBy", headerName: t("UpdatedBy"), flex: 2 },
      { field: "updatedDate", headerName: t("UpdateDate"), flex: 1.5 },
      { field: "writeDate", headerName: t("WriteDate"), flex: 1.5 },
      { field: "eventId", headerName: t("EventID"), flex: 2 },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-2">
        {t("UserAccountID")}: {userAccountId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={userAccountHistory}
        getRowId={(params) => String(params.data.id)}
        // Normal layout for pagination
        domLayout="autoHeight"
        // Enable pagination
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
        // Fixed height for consistent UI
        height="400px"
        animateRows={true}
      />
    </div>
  );
};

export default React.memo(UserAccountHistory);

